import type { FieldDef, FormDefinition } from './types';

/**
 * Effective definition = template version + custom fields: a custom field goes right after its `placeAfter`
 * top-level field (null: at the beginning); same anchor → array order; fields anchored to a custom field follow it.
 * Custom fields with an unknown anchor are appended at the end (reported by the validator). Same as the backend.
 */
export function mergeDefinition(template: FormDefinition, customFields: FieldDef[] | null | undefined): FormDefinition {
  if (!customFields || customFields.length === 0) return template;
  const byAnchor = new Map<string, FieldDef[]>();
  for (const c of customFields) {
    const anchor = c.placeAfter ?? '';
    byAnchor.set(anchor, [...(byAnchor.get(anchor) ?? []), c]);
  }
  const result: FieldDef[] = [];
  const emitted = new Set<FieldDef>();
  const emit = (f: FieldDef) => {
    if (emitted.has(f)) return;
    emitted.add(f);
    result.push(f);
    for (const c of byAnchor.get(f.key) ?? []) emit(c);
  };
  for (const c of byAnchor.get('') ?? []) emit(c);
  for (const t of template.fields) emit(t);
  for (const c of customFields) emit(c);
  return { fields: result };
}

/** All fields in pre-order (sections and groups before their children). */
export function flatten(fields: FieldDef[]): FieldDef[] {
  return fields.flatMap((f) => [f, ...flatten(f.children ?? [])]);
}
